import AuthModule from "@/store/modules/auth";
import axios, {
  AxiosInstance,
  InternalAxiosRequestConfig,
  AxiosResponse,
  AxiosError,
} from "axios";
import { redirectToLoginPage } from "./helpers/router.helper";
import { SharedCallbacks } from "./shared-resources";
import { updateToken } from "@/permission";
import { ModalStatus } from "@/constants/modal";
import { showSessionExpiredPopup } from "./helpers/auth";
import { LogService } from "@/services/logging";
import { endpoints, EndpointEnvVar } from "@/config/endpoints";
import { resolveEndpoint } from "@/config/endpoint-resolver";

// const
const apiURL = endpoints.VITE_ROOT_API;
const smtzAuthApiURL = endpoints.VITE_API2;
const servicesApiURL = endpoints.VITE_SERVICES_API;
const DEFAULT_LANG_API = "en";

// check-promocode answers 401 for a business rule (expired or out-of-day code),
// not an authentication failure.
const BUSINESS_AUTH_STATUS_PATHS = ["/check-promocode"];

function isBusinessAuthStatusUrl(url?: string): boolean {
  return !!url && BUSINESS_AUTH_STATUS_PATHS.some((path) => url.includes(path));
}

class RequestBasic {
  public service: AxiosInstance;

  constructor(
    opts: {
      headers: {
        common: { "x-api-key": string | undefined };
        post: { "Content-Type": string };
      };
      baseURL: string | undefined;
      timeout: number;
    },
    endpointVar?: EndpointEnvVar
  ) {
    // @ts-ignore
    this.service = axios.create(opts);
    // Per-request base URL override. Registered before reqSuccess so it runs
    // after it (axios reverse order), keeping reqSuccess's static-baseURL check intact.
    if (endpointVar) {
      this.service.interceptors.request.use((config) => {
        config.baseURL = resolveEndpoint(endpointVar);
        return config;
      });
    }
    // Request interceptors
    this.service.interceptors.request.use(this.reqSuccess, this.reqError);
    // Response interceptors
    this.service.interceptors.response.use(this.resSuccess, this.resError);
  }

  private async reqSuccess(config: InternalAxiosRequestConfig) {
    const url = String(config.url);
    if (config.baseURL !== smtzAuthApiURL)
      config.url = `${DEFAULT_LANG_API}${config.url}`;

    if (SharedCallbacks.authHeaderCallback) {
      try {
        await SharedCallbacks.authHeaderCallback(config);
      } catch (error) {
        LogService.info("Error while invoking auth builder ", { error });
      }
    }

    if (SharedCallbacks.startTimeMetadataCallback) {
      SharedCallbacks.startTimeMetadataCallback(config);
    }

    // TODO @Riyadh, remove in future commented code below
    // const authorization = config.headers?.Authorization;
    // if (AuthModule.token) {
    //   config.headers!.Authorization =
    //     authorization || `Bearer ${getUserAccessToken()}`;
    // } else {
    //   Promise.reject();
    // }

    try {
      await updateToken();
    } catch (error) {
      LogService.info("Error while updating token", { error });
    }

    return config;
  }

  private reqError(error: Error) {
    return Promise.reject(error);
  }

  private resSuccess(response: AxiosResponse) {
    if (SharedCallbacks.monitorLatencyCallback) {
      SharedCallbacks.monitorLatencyCallback(response);
    }

    return response;
  }

  private resError(axiosError: AxiosError) {
    const response = axiosError.response;
    if (SharedCallbacks.monitorLatencyCallback && response) {
      SharedCallbacks.monitorLatencyCallback(response, axiosError);
    }
    // Only an authentication failure ends the session: a 403 means the account
    // lacks rights here, and these paths answer 401 for a business rule.
    if (
      response?.status === 401 &&
      !isBusinessAuthStatusUrl(response.config?.url)
    ) {
      LogService.info(
        "Logging user out after an Unauthorized response by backend",
        { url: response.config?.url }
      );
      showSessionExpiredPopup();
    }
    return Promise.reject(axiosError);
  }
}

const request = new RequestBasic(
  {
    baseURL: apiURL,
    timeout: 120000,
    headers: {
      common: {
        "x-api-key": import.meta.env.VITE_X_API_KEY,
      },
      post: {
        "Content-Type": "application/json",
      },
    },
  },
  "VITE_ROOT_API"
);

export default request.service;

const smtzRequest = new RequestBasic(
  {
    baseURL: smtzAuthApiURL,
    timeout: 120000,
    headers: {
      common: {
        "x-api-key": import.meta.env.VITE_X_API_KEY,
      },
      post: {
        "Content-Type": "application/json",
      },
    },
  },
  "VITE_API2"
);
export const request2 = smtzRequest.service;

/**
 * Request for services API
 */
export const requestServices = axios.create({
  baseURL: servicesApiURL,
  timeout: 120000,
  headers: {
    "Content-Type": "application/json",
  },
});
// Per-request base URL override (Flagsmith), fail-closed to static.
requestServices.interceptors.request.use((config) => {
  config.baseURL = resolveEndpoint("VITE_SERVICES_API");
  return config;
});

export const cognitoRequestService = axios.create({
  baseURL: endpoints.VITE_COGNITO_API_URL,
  timeout: 20000,
  headers: {
    "Content-Type": "application/json",
    "x-api-key": String(import.meta.env.VITE_COGNITO_X_API_KEY),
  },
});
// Per-request base URL override (Flagsmith), fail-closed to static.
cognitoRequestService.interceptors.request.use((config) => {
  config.baseURL = resolveEndpoint("VITE_COGNITO_API_URL");
  return config;
});

/**
 * Request used for logging
 */
export const request3 = axios.create({
  baseURL: import.meta.env.VITE_LOG_API,
  timeout: 20000,
  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * Request for B2C API
 * used only once to pull the list of countries
 */
const b2cApiURL = endpoints.VITE_B2C_API;
// export const requestB2C = axios.create({
//   baseURL: apiURL,
//   headers: {
//     "x-api-key": process.env.VUE_APP_B2C_X_API_KEY,
//   },
// });
const requestB2CService = new RequestBasic(
  {
    baseURL: b2cApiURL,
    timeout: 120000,
    headers: {
      common: {
        "x-api-key": import.meta.env.VITE_B2C_X_API_KEY,
      },
      post: {
        "Content-Type": "application/json",
      },
    },
  },
  "VITE_B2C_API"
);
export const requestB2C = requestB2CService.service;
